import type { Auth } from 'firebase/auth';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

export interface ReauthenticateAndChangePasswordOptions {
  /** A instância `Auth` do app — cada um inicializa a sua (emulador, config). */
  auth: Auth;
  input: ChangePasswordInput;
  /**
   * A chamada de troca no backend. É porta, e não implementação, porque o
   * pacote não conhece o `apiClient` de cada app (base URL, interceptores,
   * headers de scope/plataforma) nem deve conhecer.
   *
   * Recebe o `input` inteiro, mas o backend hoje aceita só `{ newPassword }`
   * (`ChangePasswordRequestDto` + `forbidNonWhitelisted`); a `currentPassword`
   * existe para a reautenticação no Firebase, acima, e não trafega.
   */
  changePassword: (input: ChangePasswordInput) => Promise<void>;
}

/**
 * Troca obrigatória (senha temporária): reautentica no Firebase com a senha
 * atual — a temporária —, troca pela nova e devolve o e-mail.
 *
 * Devolve o e-mail porque quem chama precisa dele para o re-login: trocar a
 * senha **revoga todas as sessões**, inclusive esta, e o `currentUser` deixa de
 * ser fonte confiável logo em seguida.
 */
export async function reauthenticateAndChangePassword({
  auth,
  input,
  changePassword,
}: ReauthenticateAndChangePasswordOptions): Promise<string> {
  const user = auth.currentUser;
  if (!user?.email) {
    throw new Error('Sessão expirada. Entre novamente.');
  }
  const email = user.email;

  const credential = EmailAuthProvider.credential(email, input.currentPassword);
  await reauthenticateWithCredential(user, credential);

  // Refresh forçado: sem isso o interceptor do api-client reenviaria o token em
  // cache (com o `auth_time` antigo) e o back recusaria com 403.
  await user.getIdToken(true);

  await changePassword(input);
  return email;
}
